// CMDB — Configuration Management Database. Org-level asset register: CI types,
// Configuration Items with free-form attributes, relationships, and an impact
// (blast-radius) view. CIs are linkable from Service Desk tickets and Jira
// tasks via CiLinkPanel.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';
import {
  HardDrives, Stack, CloudCheck, Database, Laptop, WifiHigh, Cube, Plus, MagnifyingGlass,
  X, Trash, Cpu, Warning, ArrowsLeftRight,
} from '@phosphor-icons/react';

const ICONS = { HardDrives, Stack, CloudCheck, Database, Laptop, WifiHigh, Cube, Cpu };
const CiIcon = ({ name, ...p }) => { const I = ICONS[name] || Cube; return <I {...p} />; };
const STATUS_TONE = { active: 'bg-emerald-50 text-emerald-700 ring-emerald-200', maintenance: 'bg-amber-50 text-amber-700 ring-amber-200', retired: 'bg-zinc-100 text-zinc-500 ring-zinc-200' };
const REL_LABEL = { depends_on: 'Depends on', runs_on: 'Runs on', connected_to: 'Connected to', part_of: 'Part of' };

export default function CmdbPage() {
  const { API, getOrgHeaders, currentOrg } = useAuth();
  const { caps } = useSubscription();
  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);
  const sdEnabled = caps?.service_desk_enabled === true;

  const [types, setTypes] = useState([]);
  const [cis, setCis] = useState([]);
  const [portals, setPortals] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [queueFilter, setQueueFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // CI being created/edited (dialog)
  const [detail, setDetail] = useState(null);      // CI id opened in drawer

  const typeById = useMemo(() => Object.fromEntries(types.map(t => [t.ci_type_id, t])), [types]);
  const portalById = useMemo(() => Object.fromEntries(portals.map(p => [p.portal_id, p])), [portals]);

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const [t, c, p] = await Promise.all([
        axios.get(`${API}/cmdb/ci-types`, h()),
        axios.get(`${API}/cmdb/cis`, h()),
        axios.get(`${API}/service-desk/portals`, h()).catch(() => ({ data: [] })),
      ]);
      setTypes(t.data || []); setCis(c.data || []); setPortals(p.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [currentOrg, sdEnabled, API, h]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cis.filter(c => {
      if (typeFilter !== 'all' && c.ci_type_id !== typeFilter) return false;
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (queueFilter === 'unassigned' && (c.portal_ids || []).length) return false;
      if (queueFilter !== 'all' && queueFilter !== 'unassigned' && !(c.portal_ids || []).includes(queueFilter)) return false;
      if (q && !(`${c.name} ${c.description || ''}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [cis, typeFilter, statusFilter, queueFilter, query]);

  const saveCi = async (ci) => {
    try {
      if (ci.ci_id) {
        const { data } = await axios.patch(`${API}/cmdb/cis/${ci.ci_id}`, ci, h());
        setCis(prev => prev.map(x => x.ci_id === data.ci_id ? data : x));
      } else {
        const { data } = await axios.post(`${API}/cmdb/cis`, ci, h());
        setCis(prev => [data, ...prev]);
      }
      toast.success('Item saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
  };

  if (!sdEnabled) return <ServiceDeskUpsell />;

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="cmdb-page">
      <div className="max-w-[1500px] mx-auto px-8 py-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-cyan-600 uppercase tracking-wider mb-1"><Cube size={14} weight="fill" /> CMDB</div>
            <h1 className="text-2xl font-bold text-zinc-900 font-['Outfit']">Assets &amp; Configuration Items</h1>
            <p className="text-sm text-zinc-500 mt-0.5">Your service infrastructure — servers, apps, services and their dependencies.</p>
          </div>
          <Button className="shrink-0" onClick={() => setEditing({ ci_type_id: types[0]?.ci_type_id || null, status: 'active', attributes: {} })} data-testid="cmdb-new-ci-btn">
            <Plus size={14} className="mr-1.5" /> New item
          </Button>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap" data-testid="cmdb-type-filters">
          <button onClick={() => setTypeFilter('all')} data-testid="cmdb-type-all"
            className={`px-3 py-1.5 rounded-md text-xs font-medium ring-1 transition-colors ${typeFilter === 'all' ? 'bg-zinc-900 text-white ring-zinc-900' : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50'}`}>
            All items <span className="opacity-60">· {cis.length}</span>
          </button>
          {types.map(t => (
            <button key={t.ci_type_id} onClick={() => setTypeFilter(t.ci_type_id)} data-testid={`cmdb-type-${t.ci_type_id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium ring-1 transition-colors ${typeFilter === t.ci_type_id ? 'bg-zinc-900 text-white ring-zinc-900' : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50'}`}>
              <CiIcon name={t.icon} size={13} style={{ color: typeFilter === t.ci_type_id ? '#fff' : t.color }} />
              {t.name} <span className="opacity-60">· {t.ci_count}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search items…" data-testid="cmdb-search"
              className="w-full h-9 pl-9 pr-3 rounded-md border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-300" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="cmdb-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>
          <Select value={queueFilter} onValueChange={setQueueFilter}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="cmdb-queue-filter"><SelectValue placeholder="All queues" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All queues</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {portals.map(p => <SelectItem key={p.portal_id} value={p.portal_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-zinc-400 ml-auto">{filtered.length} item{filtered.length === 1 ? '' : 's'}</span>
        </div>

        {loading ? (
          <div className="text-center py-16 text-zinc-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
            <Cube size={36} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-sm text-zinc-500 mb-4">No configuration items yet. Add your first asset to build the CMDB.</p>
            <Button onClick={() => setEditing({ ci_type_id: types[0]?.ci_type_id || null, status: 'active', attributes: {} })}>New item</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cmdb-ci-grid">
            {filtered.map(c => {
              const t = typeById[c.ci_type_id];
              return (
                <button key={c.ci_id} onClick={() => setDetail(c.ci_id)} data-testid={`cmdb-ci-${c.ci_id}`}
                  className="text-left bg-white ring-1 ring-zinc-200 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
                  <div className="flex items-start justify-between">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: (t?.color || '#2563eb') + '18' }}>
                      <CiIcon name={t?.icon} size={18} style={{ color: t?.color || '#2563eb' }} />
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ring-1 capitalize ${STATUS_TONE[c.status]}`}>{c.status}</span>
                  </div>
                  <p className="mt-2.5 font-semibold text-zinc-900 truncate">{c.name}</p>
                  <p className="text-[11px] text-zinc-400">{t?.name || 'Item'}</p>
                  {c.description && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{c.description}</p>}
                  {(c.portal_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2" data-testid={`cmdb-ci-queues-${c.ci_id}`}>
                      {c.portal_ids.slice(0, 3).map(pid => portalById[pid] && (
                        <span key={pid} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100">{portalById[pid].name}</span>
                      ))}
                      {c.portal_ids.length > 3 && <span className="text-[10px] text-zinc-400">+{c.portal_ids.length - 3}</span>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {editing && <CiEditDialog ci={editing} types={types} portals={portals} onClose={() => setEditing(null)} onSave={saveCi} />}
      {detail && <CiDetailDrawer ciId={detail} types={types} typeById={typeById} portalById={portalById} h={h} API={API}
        onClose={() => setDetail(null)} onEdit={(ci) => { setDetail(null); setEditing(ci); }} onChanged={load} allCis={cis} />}
    </div>
  );
}

function CiEditDialog({ ci, types, portals, onClose, onSave }) {
  const [form, setForm] = useState({ ...ci, attributes: ci.attributes || {}, portal_ids: ci.portal_ids || [] });
  const [attrRows, setAttrRows] = useState(Object.entries(ci.attributes || {}).map(([k, v]) => ({ k, v })));
  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const togglePortal = (pid) => setForm(f => ({ ...f, portal_ids: (f.portal_ids || []).includes(pid) ? f.portal_ids.filter(x => x !== pid) : [...(f.portal_ids || []), pid] }));
  const submit = () => {
    const attributes = {};
    attrRows.forEach(r => { if (r.k.trim()) attributes[r.k.trim()] = r.v; });
    if (!form.name?.trim()) return;
    onSave({ ...form, attributes });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cmdb-ci-dialog">
        <DialogHeader>
          <DialogTitle>{ci.ci_id ? 'Edit item' : 'New configuration item'}</DialogTitle>
          <DialogDescription>Describe the asset and its attributes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-auto pr-1">
          <div><Label className="text-xs">Name</Label><Input value={form.name || ''} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. prod-web-01" data-testid="cmdb-ci-name" autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Type</Label>
              <Select value={form.ci_type_id || ''} onValueChange={(v) => set({ ci_type_id: v })}>
                <SelectTrigger data-testid="cmdb-ci-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{types.map(t => <SelectItem key={t.ci_type_id} value={t.ci_type_id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                <SelectTrigger data-testid="cmdb-ci-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem><SelectItem value="retired">Retired</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">Description</Label><Textarea value={form.description || ''} onChange={(e) => set({ description: e.target.value })} rows={2} /></div>
          {portals.length > 0 && (
            <div data-testid="cmdb-ci-queues-picker">
              <Label className="text-xs">Serves queues</Label>
              <p className="text-[11px] text-zinc-400 mb-1.5">Associate this asset with the queues whose teams rely on it. Leave empty to keep it org-wide only.</p>
              <div className="flex flex-wrap gap-1.5">
                {portals.map(p => {
                  const on = (form.portal_ids || []).includes(p.portal_id);
                  return (
                    <button type="button" key={p.portal_id} onClick={() => togglePortal(p.portal_id)} data-testid={`cmdb-ci-queue-${p.portal_id}`}
                      className={`text-xs px-2.5 py-1 rounded-md ring-1 transition-colors ${on ? 'bg-cyan-600 text-white ring-cyan-600' : 'bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50'}`}>
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1"><Label className="text-xs">Attributes</Label>
              <button onClick={() => setAttrRows(r => [...r, { k: '', v: '' }])} className="text-xs text-cyan-600 font-medium" data-testid="cmdb-add-attr">+ Add attribute</button></div>
            <div className="space-y-2">
              {attrRows.map((r, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={r.k} onChange={(e) => setAttrRows(rows => rows.map((x, j) => j === i ? { ...x, k: e.target.value } : x))} placeholder="Key (e.g. IP)" className="h-8 text-sm" />
                  <Input value={r.v} onChange={(e) => setAttrRows(rows => rows.map((x, j) => j === i ? { ...x, v: e.target.value } : x))} placeholder="Value" className="h-8 text-sm" />
                  <button onClick={() => setAttrRows(rows => rows.filter((_, j) => j !== i))} className="text-zinc-400 hover:text-red-500"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!form.name?.trim()} data-testid="cmdb-ci-save">Save item</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CiDetailDrawer({ ciId, types, typeById, portalById, h, API, onClose, onEdit, onChanged, allCis }) {
  const [ci, setCi] = useState(null);
  const [impact, setImpact] = useState([]);
  const [relTo, setRelTo] = useState('');
  const [relType, setRelType] = useState('depends_on');
  const load = useCallback(async () => {
    try {
      const [d, im] = await Promise.all([axios.get(`${API}/cmdb/cis/${ciId}`, h()), axios.get(`${API}/cmdb/cis/${ciId}/impact`, h())]);
      setCi(d.data); setImpact(im.data || []);
    } catch (e) { toast.error('Failed to load item'); }
  }, [ciId, API, h]);
  useEffect(() => { load(); }, [load]);

  const addRel = async () => {
    if (!relTo) return;
    try { await axios.post(`${API}/cmdb/cis/${ciId}/relationships`, { to_ci: relTo, type: relType }, h()); setRelTo(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delRel = async (relId) => { await axios.delete(`${API}/cmdb/relationships/${relId}`, h()); load(); };
  const delCi = async () => { if (!window.confirm('Delete this item and its links?')) return; await axios.delete(`${API}/cmdb/cis/${ciId}`, h()); toast.success('Item deleted'); onChanged(); onClose(); };

  const t = ci && typeById[ci.ci_type_id];
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cmdb-ci-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-auto shadow-2xl">
        {!ci ? <div className="p-8 text-zinc-400">Loading…</div> : (
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: (t?.color || '#2563eb') + '18' }}><CiIcon name={t?.icon} size={22} style={{ color: t?.color || '#2563eb' }} /></div>
                <div>
                  <h2 className="text-lg font-bold text-zinc-900">{ci.name}</h2>
                  <p className="text-xs text-zinc-400">{t?.name} · <span className="capitalize">{ci.status}</span></p>
                </div>
              </div>
              <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
            </div>
            {ci.description && <p className="text-sm text-zinc-600 mb-4">{ci.description}</p>}

            {(ci.portal_ids || []).length > 0 && (
              <div className="mb-5"><p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">Serves queues</p>
                <div className="flex flex-wrap gap-1.5" data-testid="cmdb-drawer-queues">
                  {ci.portal_ids.map(pid => (
                    <span key={pid} className="text-xs font-medium px-2 py-0.5 rounded-md bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100">{portalById[pid]?.name || 'Queue'}</span>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(ci.attributes || {}).length > 0 && (
              <div className="mb-5"><p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">Attributes</p>
                <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-100">
                  {Object.entries(ci.attributes).map(([k, v]) => (<div key={k} className="flex justify-between px-3 py-2 text-sm"><span className="text-zinc-500">{k}</span><span className="text-zinc-800 font-medium">{String(v)}</span></div>))}
                </div>
              </div>
            )}

            <div className="mb-5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-1"><ArrowsLeftRight size={13} /> Relationships</p>
              <div className="space-y-1.5 mb-2">
                {(ci.relationships || []).length === 0 && <p className="text-xs text-zinc-400">No relationships yet.</p>}
                {(ci.relationships || []).map(r => (
                  <div key={r.rel_id} className="flex items-center justify-between text-sm bg-zinc-50 rounded-md px-3 py-1.5">
                    <span className="text-zinc-600"><span className="text-zinc-400">{r.direction === 'out' ? REL_LABEL[r.type] : `${REL_LABEL[r.type]} ←`}</span> <span className="font-medium text-zinc-800">{r.other?.name || '—'}</span></span>
                    <button onClick={() => delRel(r.rel_id)} className="text-zinc-300 hover:text-red-500"><X size={13} /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-1.5">
                <Select value={relType} onValueChange={setRelType}><SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(REL_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                <Select value={relTo} onValueChange={setRelTo}><SelectTrigger className="h-8 flex-1 text-xs" data-testid="cmdb-rel-target"><SelectValue placeholder="Select item…" /></SelectTrigger>
                  <SelectContent>{allCis.filter(x => x.ci_id !== ciId).map(x => <SelectItem key={x.ci_id} value={x.ci_id}>{x.name}</SelectItem>)}</SelectContent></Select>
                <Button size="sm" className="h-8" onClick={addRel} disabled={!relTo} data-testid="cmdb-rel-add">Link</Button>
              </div>
            </div>

            {impact.length > 0 && (
              <div className="mb-5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1"><Warning size={13} weight="fill" /> Impact if this fails ({impact.length})</p>
                <div className="rounded-lg border border-amber-200 bg-amber-50/50 divide-y divide-amber-100">
                  {impact.map(x => <div key={x.ci_id} className="px-3 py-1.5 text-sm text-zinc-700">{x.name}</div>)}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-zinc-100">
              <Button variant="outline" size="sm" onClick={() => onEdit(ci)} data-testid="cmdb-ci-edit">Edit</Button>
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={delCi} data-testid="cmdb-ci-delete"><Trash size={14} className="mr-1" /> Delete</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
